/*

PW Text Overflow Handling (single string center-ellipsit
Truncates from center of string, displaying start and end

*/

import React from 'react'
import T from 'prop-types'
import DimensionAware from '../hoc/DimensionAware'
import Timing from '../hoc/Timing'
import ReactDOM from 'react-dom'
import TextOverflowOverlay from './TextOverflowOverlay'

const GLOBAL_HIDE_EVENTS = ['mousewheel', 'mousedown']

class TextOverflowCenter extends React.Component {
  static displayName = 'TextOverflowCenter'

  static propTypes = {
    tagName: T.string,
    className: T.string,
    height: T.number,
    width: T.number,
    value: T.string, // Only accepts single string children

    setTimer: T.func.isRequired,
    clearTimer: T.func.isRequired,
    delay: T.number,

    onMouseEnter: T.func,
    onMouseLeave: T.func,
    overlayClassName: T.string,
    watchMove: T.bool
  }

  static defaultProps = {
    tagName: 'div',
    height: 10,
    width: 10,
    delay: 500,
    watchMove: false
  }

  state = {
    renderValue: [''],
    isTruncated: false
  }

  componentDidMount() {
    this._checkOverflow()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.height !== this.props.height ||
      nextProps.width !== this.props.width ||
      nextProps.value !== this.props.value
    ) {
      this._checkOverflow()
    }
  }

  componentWillUnmount() {
    this._destroyOverlay()
  }

  _checkOverflow = () => {
    const {
      value
    } = this.props

    const thisEl = ReactDOM.findDOMNode(this)

    if (!thisEl) {
      return
    }

    const { clientWidth } = thisEl
    const {fontSize} = window.getComputedStyle(thisEl)
    let container = window.document.createElement('span')
    let testChar = window.document.createTextNode('_')
    container.className = "text_overflow_center_ellipsis_measure_node"
    container.style.fontSize = fontSize
    container.appendChild(testChar)
    window.document.body.appendChild(container)

    const singleCharWidth = container.clientWidth
    const maxChars = Math.floor(clientWidth / Math.max(singleCharWidth, 1)) - 4

    // Cleanup
    testChar = null
    if (container.parentNode) {
      container.parentNode.removeChild(container)
    }
    container = null

    // Split/truncate if needed
    if (value && maxChars > 1 && value.length > maxChars) {
      // truncate
      let out = ""
      if (maxChars === 1) {
        out = [
          value.substring(0, 1),
          ''
        ]
      }
      else {
        var midPt = Math.ceil(value.length / 2)
        var stripCt = value.length - maxChars
        var stripLeft = Math.ceil(stripCt / 2)
        var stripRight = stripCt - stripLeft - 1 // Subtract 1 extra to make room for the ellipsis
        out = [
          value.substring(0, midPt - stripLeft),
          value.substring(midPt + stripRight)
        ]
      }

      this.setState({
        renderValue: out,
        isTruncated: true
      })
    }
    else {
      this.setState({
        renderValue: [value],
        isTruncated: false
      })
    }
  }

  _onMouseEnter = e => {
    const {
      onMouseEnter,
      delay
    } = this.props
    const {
      isTruncated
    } = this.state
    if (onMouseEnter) {
      onMouseEnter(e)
    }
    if (isTruncated) {
      if (delay) {
        this.props.setTimer(this._showOverlay, delay, 'delay')
      } else {
        this._showOverlay()
      }
    }
  }

  _onMouseLeave = e => {
    const {
      onMouseLeave
    } = this.props
    if (onMouseLeave) {
      onMouseLeave(e)
    }
    this._destroyOverlay()
  }

  _showOverlay = () => {
    const {
      value,
      overlayClassName
    } = this.props
    this._destroyOverlay() //just in case
    const el = ReactDOM.findDOMNode(this)

    const mountNode = (this._overlayMountNode = document.createElement('div'))
    mountNode.className = 'text_overflow_overlay_mount'
    document.body.appendChild(mountNode)

    ReactDOM.render(
      <TextOverflowOverlay
        source={el}
        content={value}
        className={overlayClassName}
      />,
      mountNode
    )
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.addEventListener(name, this._destroyOverlay, true)
    )

    if (this.props.watchMove) {
      this._watchMove()
    }
  }

  _watchMove = () => {
    const box = ReactDOM.findDOMNode(this).getBoundingClientRect()
    const origBox = this._origBBox
    if (origBox) {
      if (
        box.left !== origBox.left ||
        box.top !== origBox.top ||
        box.right !== origBox.right ||
        box.bottom !== origBox.bottom
      ) {
        this._destroyOverlay()
        return
      }
    } else {
      this._origBBox = box
    }
    this.props.setTimer(this._watchMove, 50, 'watchMove')
  }


  _destroyOverlay = () => {
    const mountNode = this._overlayMountNode
    if (mountNode && mountNode.parentNode) {
      ReactDOM.unmountComponentAtNode(mountNode)
      mountNode.parentNode.removeChild(mountNode)
      delete this._overlayMountNode
    }
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.removeEventListener(name, this._destroyOverlay, true)
    )
    this.props.clearTimer('delay')
    this.props.clearTimer('watchMove')
    delete this._origBBox
  }

  render() {
    const {
      className,
      value,
      tagName
    } = this.props
    const {
      renderValue,
      isTruncated
    } = this.state
    const Comp = tagName

    return (
      <Comp
        className={`text_overflow_center_ellipsis ${className || ''}`}
        onMouseEnter={this._onMouseEnter}
        onTouchStart={this._onMouseEnter}
        onMouseLeave={this._onMouseLeave}
        onTouchEnd={this._onMouseLeave}
      >
        {renderValue.length === 1 ? renderValue[0] : (
          <span className="rendered_str">
            {renderValue[0]}
            <span className="ellipsis">&hellip;</span>
            {renderValue[1] || ''}
          </span>
        )}
        {isTruncated &&
          <span className="copy_only_hidden">{value}</span>
        }
      </Comp>
    )
  }
}

export default DimensionAware(Timing(TextOverflowCenter))
